<script>
    import { createEventDispatcher } from "svelte";
    import { PRConnect } from "@projectroom/ui-lib";
    import axios from "axios";
    import { Section } from "../../lib/ui";

    /**
     * 링크 프리뷰 = 게임 주소를 카톡·슬랙·X 에 붙여넣었을 때 뜨는 **공유 카드**.
     * 서버 buildGameHtml 이 이 값을 셸 <head> 의 og 태그로 굽는다(Bot.js).
     *
     * ⚠️ 저장소가 다른 탭과 **다르다** — BOT_GAME_SETTINGS 가 아니라 **BOTS 한 행**이다
     *    (THUMBNAIL_IMAGE_CONTENT / _TITLE / _SUB_TITLE). 봇 빌더 [설정] 탭과 같은 칸을 쓴다.
     *    그래서 저장도 modifyGameSettings 가 아니라 modifyBotStatisticsData 로 나간다
     *    (GameBuilder.saveBotProfile — 거기 ⚠️ 주석을 꼭 읽을 것).
     * ⚠️ 그림은 자산 라이브러리(assetId)가 **아니다**. 컬럼에 담기는 건 파일 이름뿐이고
     *    서버가 `{botId}/common/thumbnail-image/{파일명}` 으로 주소를 조립한다.
     *    여기에 assetId 를 넣으면 og:image 주소가 통째로 깨진다.
     */
    export let bot;

    const dispatch = createEventDispatcher();

    const S3_BASE = "https://groovworks-avatalk-public-file-storage.s3.ap-northeast-2.amazonaws.com";
    // 서버가 그림·제목이 비었을 때 쓰는 값(Bot.js buildGameHtml). 미리보기도 **같은 값**을
    // 보여줘야 한다 — 여기서만 게임 제목으로 채워 보여주면 실제로 공유했을 때 딴 게 나온다.
    const DEFAULT_IMAGE = `${S3_BASE}/common/static/images/avatalk_meta_image.png`;
    const DEFAULT_TITLE = "AVATALK - 아바톡";
    // og 수집기(카톡·슬랙)가 큰 그림을 건너뛰는 선. 넘으면 카드가 그림 없이 뜬다.
    const MAX_BYTES = 5 * 1024 * 1024;

    let fileInput;
    let uploading = false;
    let uploadError = "";
    // 같은 이름으로 다시 올리면 주소가 그대로라 브라우저가 옛 그림을 계속 쓴다 →
    // **빌더 썸네일만** 캐시를 깬다(og:image 주소에는 붙이지 않는다. 수집기가 쿼리를 싫어한다).
    let stamp = 0;

    $: savedImageUrl = bot.thumbnailImageContent
        ? `${S3_BASE}/${bot.id}/common/thumbnail-image/${bot.thumbnailImageContent}`
        : "";
    $: shownImageUrl = savedImageUrl
        ? `${savedImageUrl}${stamp ? `?t=${stamp}` : ""}`
        : DEFAULT_IMAGE;
    $: shownTitle = bot.thumbnailImageTitle || DEFAULT_TITLE;
    $: shownDesc = bot.thumbnailImageSubTitle || "";
    // 카드 아래 회색 줄에 뜨는 도메인. pageUrl 이 아직 없으면(= 게시 전) 비워 둔다.
    $: shownHost = (() => {
        try {
            return bot.pageUrl ? new URL(bot.pageUrl).host : "";
        } catch {
            return "";
        }
    })();

    const emit = (patch) => dispatch("change", patch);

    const setText = (key, value) => {
        const next = value.trim();
        if ((bot[key] || "") === next) return; // 안 바뀌었으면 저장 왕복을 아낀다
        emit({ [key]: next || null });
    };

    /**
     * 그림 올리기. 자산 라이브러리를 안 쓰므로(위 주석) 예전 경로에 직접 PUT 한다.
     *
     * ⚠️ 파일 이름을 **그대로 쓰지 않는다.** macOS 에서 올린 한글 파일명은 S3 에 NFD 로 들어가고
     *    DB·HTML 에는 NFC 로 적혀 403 이 난다(게임·허브·기업형 공통 함정). 어차피 이름이
     *    화면에 안 보이므로 `og-{시각}.{확장자}` 로 눕혀 그 길을 아예 막는다.
     */
    const onFileSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // 같은 파일을 다시 골라도 change 가 오도록
        if (!file) return;

        uploadError = "";
        if (!file.type?.startsWith("image/")) {
            uploadError = "이미지 파일만 올릴 수 있어요.";
            return;
        }
        if (file.size > MAX_BYTES) {
            uploadError = "5MB 이하 이미지로 올려주세요. (더 크면 공유 카드에 그림이 안 나옵니다)";
            return;
        }

        const ext = (file.name.split(".").pop() || "").toLowerCase().replace(/[^a-z0-9]/g, "") || "png";
        const name = `og-${Date.now()}.${ext}`;
        const key = `${bot.id}/common/thumbnail-image/${name}`;
        const type = file.type || "application/octet-stream";

        uploading = true;
        PRConnect.send(
            "/api/File/uploadPresignedUrl",
            { key, type },
            async (data) => {
                // PRConnect 는 HTTP 200 이면 성공 콜백을 부른다 — 서버가 본문에 실어 보낸
                // header.status 를 따로 봐야 한다(안 보면 undefined 를 PUT 주소로 쓰게 된다).
                if (data.header?.status !== "success" || !data.result) {
                    uploading = false;
                    uploadError = "이미지를 올리지 못했어요. 잠시 후 다시 시도해주세요.";
                    return;
                }
                try {
                    await axios.put(data.result, file, { headers: { "Content-Type": type } });
                    stamp = Date.now();
                    emit({ thumbnailImageContent: name });
                } catch (err) {
                    uploadError = "이미지를 올리지 못했어요. 잠시 후 다시 시도해주세요.";
                } finally {
                    uploading = false;
                }
            },
            () => {
                uploading = false;
                uploadError = "이미지를 올리지 못했어요. 잠시 후 다시 시도해주세요.";
            },
        );
    };

    const clearImage = () => emit({ thumbnailImageContent: null });
</script>

<Section
    title="링크 프리뷰"
    description="게임 주소를 카톡·슬랙·문자에 붙여넣었을 때 보이는 카드입니다. 비워 두면 아바톡 기본값으로 나갑니다."
    optional
>
    <div class="tw-flex tw-flex-wrap tw-gap-5">
        <!-- 그림 -->
        <div class="tw-flex tw-w-[200px] tw-flex-col tw-gap-2">
            <!-- ⚠️ crossorigin 필수: 없으면 브라우저가 Origin 없는 응답을 캐시에 넣고,
                 뒤이은 게임(Phaser XHR)이 그 캐시를 재사용해 CORS 로 막힌다(빌더 썸네일 공통 규칙). -->
            <button
                type="button"
                class="og-thumb {savedImageUrl ? 'tw-border-border' : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                on:click={() => fileInput.click()}
                disabled={uploading}
                title="링크 미리보기 이미지 올리기"
            >
                {#if savedImageUrl}
                    <img crossorigin="anonymous" class="tw-h-full tw-w-full tw-object-cover" src={shownImageUrl} alt="" />
                {:else}
                    <span class="tw-text-center tw-text-xs tw-leading-tight tw-text-muted-foreground">
                        {uploading ? "올리는 중…" : "+ 이미지 올리기"}
                    </span>
                {/if}
            </button>
            <input type="file" accept="image/*" bind:this={fileInput} on:change={onFileSelected} hidden />
            <div class="tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                가로형 1200×630 권장 · 5MB 이하
            </div>
            {#if savedImageUrl}
                <div class="tw-flex tw-gap-3">
                    <button
                        type="button"
                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-text-muted-foreground tw-underline"
                        on:click={() => fileInput.click()}
                        disabled={uploading}
                    >
                        {uploading ? "올리는 중…" : "바꾸기"}
                    </button>
                    <button
                        type="button"
                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-text-muted-foreground tw-underline"
                        on:click={clearImage}
                    >
                        비우기
                    </button>
                </div>
            {/if}
            {#if uploadError}
                <div class="tw-text-[11px] tw-leading-tight tw-text-destructive">{uploadError}</div>
            {/if}
        </div>

        <!-- 글 -->
        <div class="tw-flex tw-min-w-[260px] tw-flex-1 tw-flex-col tw-gap-4">
            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">제목</span>
                <input
                    class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                    type="text"
                    maxlength="256"
                    placeholder={`예) ${bot.title || "우리 게임"} — 지금 바로 플레이`}
                    value={bot.thumbnailImageTitle || ""}
                    on:change={(e) => setText("thumbnailImageTitle", e.target.value)}
                />
                <span class="tw-text-xs tw-text-muted-foreground">
                    비워 두면 “{DEFAULT_TITLE}” 으로 나갑니다. 브라우저 탭 이름으로도 쓰입니다.
                </span>
            </label>

            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">설명</span>
                <input
                    class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                    type="text"
                    maxlength="256"
                    placeholder="예) 한 손으로 즐기는 세로 슈팅. 지금 바로 한 판!"
                    value={bot.thumbnailImageSubTitle || ""}
                    on:change={(e) => setText("thumbnailImageSubTitle", e.target.value)}
                />
                <span class="tw-text-xs tw-text-muted-foreground">
                    카드에서 두 줄쯤 보입니다. 길면 뒤가 잘립니다.
                </span>
            </label>
        </div>
    </div>
</Section>

<Section title="이렇게 보입니다" description="실제 카드는 앱마다 조금씩 다르게 그립니다(여기 모양은 카톡 기준).">
    <div class="og-card">
        <div class="og-card-image">
            <img crossorigin="anonymous" src={shownImageUrl} alt="" />
        </div>
        <div class="tw-flex tw-flex-col tw-gap-1 tw-px-3.5 tw-py-3">
            <div class="tw-truncate tw-text-sm tw-font-semibold tw-text-foreground">{shownTitle}</div>
            {#if shownDesc}
                <div class="og-card-desc tw-text-xs tw-text-muted-foreground">{shownDesc}</div>
            {/if}
            {#if shownHost}
                <div class="tw-truncate tw-pt-0.5 tw-text-[11px] tw-text-muted-foreground">{shownHost}</div>
            {/if}
        </div>
    </div>

    <p class="tw-mt-4 tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
        ⚠️ 여기서 바꾼 내용은 <strong class="tw-text-foreground">[게시하기]</strong> 를 해야 공개 주소에 반영됩니다.
        이미 공유된 링크는 카톡·슬랙이 카드를 한동안 캐시해 두기 때문에, 바로 안 바뀌어 보일 수 있습니다.
    </p>
</Section>

<style>
    /* 올린 그림이 투명 PNG 일 수 있어 어두운 바탕에 둔다(다른 탭 슬롯 썸네일과 같은 색). */
    .og-thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        aspect-ratio: 1.91 / 1; /* og:image 권장 비율 — 잘리는 자리가 미리 보인다 */
        padding: 0;
        border-width: 1px;
        border-style: solid;
        border-radius: 12px;
        background: #0d1430;
        cursor: pointer;
        overflow: hidden;
    }

    .og-thumb:hover {
        border-color: hsl(var(--sb-primary));
    }

    .og-thumb:disabled {
        cursor: default;
        opacity: 0.6;
    }

    /* 공유 카드 흉내. 폭은 메신저 말풍선 정도로 묶어 둔다(넓으면 실제와 달라 보인다). */
    .og-card {
        width: 100%;
        max-width: 340px;
        border: 1px solid hsl(var(--sb-border));
        border-radius: 12px;
        overflow: hidden;
        background: hsl(var(--sb-card));
    }

    .og-card-image {
        aspect-ratio: 1.91 / 1;
        background: #0d1430;
    }

    .og-card-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    /* 설명은 두 줄까지 — 실제 카드도 그쯤에서 자른다. */
    .og-card-desc {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
